import { useEffect } from 'react';
import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
} from 'react-router-dom';
import XiaomaLayout from 'components/xiaoma-layout';
import Home from 'pages/home';
import ProductList from 'pages/product/list';
import Login from 'pages/login';
import {
  getLocalStorageFirstPage,
  getLocalStorageLanguage,
  getLocalStorageToken,
  LOGIN_PAGE,
} from 'commons';
import { useAppDispatch } from 'store/hooks';
import {
  setAccessorySelectOptions,
  setCollectionSelectOptionsMap,
  setColorSelectOptions,
  setCountryCodeOptions,
  setCountryOptions,
  setHardwareSelectOptions,
  setHueSelectOptions,
  setLanguage,
  setMaterialCascaderOptionMap,
  setProductCategoryCascaderOptions,
  setRankSelectOptions,
  setStaffSelectOptions,
  setStampSelectOptions,
  setTypeSelectOptions,
} from 'store/slices/globalInfoSlice';
import { useSelector } from 'react-redux';
import { initUserInfo, selectUserInfo } from 'store/slices/userInfoSlice';
import PriceTagPrint from 'components/batch-print';
import { ProductAttributeCodeNames } from 'types/pms';
import useProductAttributeCodeList from 'commons/hooks/useProductAttributeCodeList';
import { groupBy } from 'lodash-es';
import buildTreeFromProductAttributeCodeList from 'utils/buildTreeFromProductAttributeCodeList';
import useProductCategoryTree from 'commons/hooks/useProductCategoryTree';
import MemberList from 'pages/ums/member-list';
import MemberView from 'pages/ums/member-view';
import { getUmsAdminList } from 'apis/sys';
import MemberEdit from 'pages/ums/member-edit';
import MemberAdd from 'pages/ums/member-add';
import PointHistoryPage from 'pages/ums/point-history';
import OrderList from 'pages/oms/order-list';
import OrderView from 'pages/oms/order-view';
import { useCountryList } from 'apis/home';
import AdminList from 'pages/sys/admin-list';
import AdminLoginLogPage from 'pages/sys/admin-login-log';
import FileUploadLogPage from 'pages/sys/file-upload-log';
import IpBlacklistPage from 'pages/sys/ip-blacklist';
import RecyclingConsignmentList from 'pages/rrs/recycling-consignment/list';
import RecyclingConsignmentDetail from 'pages/rrs/recycling-consignment-detail';
import RecycleOrderReceptionForm from 'pages/rrs/recycling-consignment-detail/reception-form';
import ReceptionList from 'pages/rrs/reception-list';
import ConsignmentAgreementList from 'pages/rrs/consignment-agreement-list';
import ConsignmentAgreementPage from 'pages/rrs/consignment-agreement';
import AppointmentManagementList from 'pages/rrs/appointment-management-list';
import AppointmentManagementConfig from 'pages/rrs/appointment-management-config';
import OrderListMobile from 'pages/oms/order-list-mobile';
import ProductListMobile from 'pages/product/product-list-mobile';
import AddSalesLink from 'pages/product/add-sales-link';
import MemberMailTemplate from 'pages/sys/member-mail-template';
import MemberMailSend from 'pages/sys/member-mail-send';
import OmsCheckoutCounter from 'pages/oms/checkout-counter';
import FmsAccountList from 'pages/fms/account-list';
import Receipt from 'pages/prints/receipt';
import OmsOrderCreateV2 from 'pages/oms/order-create-v2';
import ProductCate from 'pages/product/product-cate';
import ProductMaterialPage from 'pages/product/product-material';
import CouponListPage from 'pages/sms/coupon-list';
import CouponHistoryListPage from 'pages/sms/coupon-history-list';
import DempyouPrint from 'pages/prints/dempyou';
import CmsArticleListPage from 'pages/cms/article-list';
import CmsArticleDetailPage from 'pages/cms/article-detail';
import ProductBoxPage from 'pages/product/product-box';
import ProductWantRequestPage from 'pages/product/product-want-request';
import ProductActivityPage from 'pages/product/product-activity';
import IntegralList from 'pages/sys/integral-list';
import IntegralDetail from 'pages/sys/integral-list/detail';
import CmsArticleCategoryPage from 'pages/cms/article-category/index';
import CmsSubjectCategoryPage from 'pages/cms/subject-category';
import CmsSubjectListPage from 'pages/cms/subject-list';
import CmsSubjectDetailPage from 'pages/cms/subject-detail';
import CmsSubjectProductRelationPage from 'pages/cms/subject-product-relation';
import Statement from 'pages/report/statement';
import DailyReportPage from 'pages/fms/daily-report';
import RoleList from 'pages/sys/role';
import MenuList from 'pages/sys/menu';
import ADList from 'pages/sms/ad/ad-list';
import ADDetail from 'pages/sms/ad/ad-detail';
import PriceLabelPrinting from 'pages/prints/price-label';
import ResourceList from 'pages/sys/resource';
import SOP from 'pages/sop';
// import ShopCash from 'pages/fms/shop-cash';
import StockTakingList from 'pages/product/stock-taking-list';
import StockTaking from 'pages/product/stock-taking';
import Attendance from './pages/report/attendance';
import DictPage from 'pages/sys/dict-page';
// import SystemSummary from './pages/fms/sys-summary';
import StoreTransfer from 'pages/product/store-transfer';
import './styles/font.css';
import CmsSpecialContentPage from './pages/cms/special-contents';
import CashSettlement from './pages/fms/cash-settlement';
import PersonalOrderList from './pages/ums/order-list';
import ProductCreatePageV2 from 'pages/product/create-page-v2';
import ProductUpdatePageV2 from 'pages/product/update-page-v2';
import RecyclingConsignmentOrderPageV2 from 'pages/rrs/recycling-consignment-order-v2';
import ProductAlbumBatchUpload from 'pages/product/album-batch-upload';
import SGOrderInvoice from 'pages/prints/sg-order-invoice';
import SGGXOrderInvoice from 'pages/prints/sg-gx-order-invoice';
import { ProfitReport } from 'pages/fms/profit-report';
import CommissionSnapshot from 'pages/fms/commission-snapshot';
import ShopSaleIncentive from 'pages/fms/shop-sale-incentive';
import OrderInvoiceV2 from 'pages/prints/order-invoice-v2';
import OmsRecycleOrderContract from 'pages/prints/oms-recycle-order-contract';
import ReceptionPrint from 'pages/prints/reception';
import ConsignmentAgreementPrint from 'pages/prints/consignment-agreement';
import { getUserInfo } from 'apis/user';
import { upsertCachedAccount } from 'commons/cached-accounts';

const names = [
  ProductAttributeCodeNames.COLOR,
  ProductAttributeCodeNames.MATERIAL,
  ProductAttributeCodeNames.STAMP,
  ProductAttributeCodeNames.RANK,
  ProductAttributeCodeNames.TYPE,
  ProductAttributeCodeNames.HUE,
  ProductAttributeCodeNames.HARDWARE,
  ProductAttributeCodeNames.ACCESSORIES,
  ProductAttributeCodeNames.COLLECTIONS,
];

function App() {
  const dispatch = useAppDispatch();
  const userInfo = useSelector(selectUserInfo);
  const [
    colorAttributeCodeList,
    materialAttributeCodeList,
    stampAttributeCodeList,
    rankAttributeCodeList,
    typeAttributeCodeList,
    hueAttributeCodeList,
    hardwareAttributeCodeList,
    accessoryAttributeCodeList,
    collectionAttributeCodeList,
  ] = useProductAttributeCodeList(names);
  const productCategoryCascaderOptions = useProductCategoryTree();
  const { countryList } = useCountryList();

  useEffect(() => {
    dispatch(setLanguage(getLocalStorageLanguage()));
    dispatch(setColorSelectOptions(colorAttributeCodeList));
    dispatch(setStampSelectOptions(stampAttributeCodeList));
    dispatch(setRankSelectOptions(rankAttributeCodeList));
    dispatch(setProductCategoryCascaderOptions(productCategoryCascaderOptions));
    dispatch(setTypeSelectOptions(typeAttributeCodeList));
    dispatch(setHueSelectOptions(hueAttributeCodeList));
    dispatch(setHardwareSelectOptions(hardwareAttributeCodeList));
    dispatch(setAccessorySelectOptions(accessoryAttributeCodeList));

    const materialProductAttributeCodeListMap = groupBy(
      materialAttributeCodeList,
      (i) => i.categoryId,
    );
    const materialCascaderOptionMap: any = {};
    Object.keys(materialProductAttributeCodeListMap).forEach((key) => {
      materialCascaderOptionMap[key] = buildTreeFromProductAttributeCodeList(
        materialProductAttributeCodeListMap[key],
      );
    });
    dispatch(setMaterialCascaderOptionMap(materialCascaderOptionMap));

    dispatch(
      setCollectionSelectOptionsMap(
        groupBy(collectionAttributeCodeList, (i) => i.categoryId),
      ),
    );

    dispatch(
      setCountryOptions(
        countryList.map(({ code, name, nameI18n }) => {
          return {
            value: code,
            label: name,
            nameI18n,
          };
        }),
      ),
    );

    dispatch(
      setCountryCodeOptions(
        countryList.map(({ areaCode, name }) => {
          return {
            value: areaCode,
            label: `+ ${areaCode} ${name}`,
          };
        }),
      ),
    );
  }, [
    colorAttributeCodeList,
    materialAttributeCodeList,
    stampAttributeCodeList,
    rankAttributeCodeList,
    productCategoryCascaderOptions,
    typeAttributeCodeList,
    hueAttributeCodeList,
    hardwareAttributeCodeList,
    accessoryAttributeCodeList,
    collectionAttributeCodeList,
    dispatch,
    countryList,
  ]);

  useEffect(() => {
    (async () => {
      const data = await getUserInfo();
      data && dispatch(initUserInfo(data));
      if (!data) return;

      // 登录成功后把当前账号 token 写入本机多账号缓存
      const token = getLocalStorageToken();
      if (token) {
        upsertCachedAccount({
          adminId: data.userId,
          username: data.username,
          nickName: data.nickName || data.username,
          token,
        });
      }

      // 下面的代码逻辑必然是用户登录了的情况
      const umsAdminList = await getUmsAdminList();

      const staffSelectOptions = umsAdminList.map(
        ({ id, nickName, shop, subShop, status }) => ({
          value: id,
          label: nickName,
          shop,
          subShop,
          status,
        }),
      );

      dispatch(setStaffSelectOptions(staffSelectOptions));
    })();
  }, [dispatch]);

  let router = createBrowserRouter(
    createRoutesFromElements(
      <Route>
        <Route path="/" element={<XiaomaLayout />}>
          <Route path="home" element={<Home />} />

          <Route path="pms/product-list" element={<ProductList />} />
          <Route
            path="pms/product-list-mobile"
            element={<ProductListMobile />}
          />
          <Route path="pms/product-add" element={<ProductCreatePageV2 />} />
          <Route
            path="pms/product-view/:id"
            element={<ProductUpdatePageV2 viewMode={true} />}
          />
          <Route
            path="pms/product-edit/:id"
            element={<ProductUpdatePageV2 viewMode={false} />}
          />
          <Route path="pms/addSalesLink" element={<AddSalesLink />} />
          <Route path="pms/product-box" element={<ProductBoxPage />} />
          <Route path="pms/product-cate" element={<ProductCate />} />
          <Route
            path="pms/product-material"
            element={<ProductMaterialPage />}
          />
          <Route path="pms/stock-taking-list" element={<StockTakingList />} />
          <Route path="pms/stock-taking/:id" element={<StockTaking />} />
          <Route path="pms/store-transfer" element={<StoreTransfer />} />
          <Route
            path="pms/album-batch-upload"
            element={<ProductAlbumBatchUpload />}
          />
          <Route
            path="pms/product-want-request"
            element={<ProductWantRequestPage />}
          />
          <Route
            path="pms/product-activity"
            element={<ProductActivityPage />}
          />

          <Route path="oms/checkout-counter" element={<OmsCheckoutCounter />} />
          <Route path="oms/order-create" element={<OmsOrderCreateV2 />} />
          <Route path="oms/order-list" element={<OrderList />} />
          <Route path="oms/order-list-mobile" element={<OrderListMobile />} />
          <Route path="oms/order-view/:id" element={<OrderView />} />

          <Route path="ums/member-list" element={<MemberList />} />
          <Route path="ums/member-view/:id" element={<MemberView />} />
          <Route path="ums/member-add" element={<MemberAdd />} />
          <Route path="ums/member-edit/:id" element={<MemberEdit />} />
          <Route path="ums/point-history" element={<PointHistoryPage />} />

          <Route
            path="sms/member-mail-template"
            element={<MemberMailTemplate />}
          />
          <Route path="sms/member-mail-send" element={<MemberMailSend />} />
          <Route path="sms/coupon-list" element={<CouponListPage />} />
          <Route
            path="sms/coupon-history-list"
            element={<CouponHistoryListPage />}
          />
          <Route path="sms/integral-list" element={<IntegralList />} />
          <Route path="sms/integral-detail/:id" element={<IntegralDetail />} />

          <Route
            path="rrs/recycling-consignment-list"
            element={
              <RecyclingConsignmentList key="recycling-consignment-list" />
            }
          />
          <Route path="rrs/reception-list" element={<ReceptionList />} />
          <Route
            path="rrs/consignment-agreement-list"
            element={<ConsignmentAgreementList />}
          />
          <Route
            path="rrs/recycling-list"
            element={
              <RecyclingConsignmentList initialType={2} key="recycling-list" />
            }
          />
          <Route
            path="rrs/consignment-list"
            element={
              <RecyclingConsignmentList
                initialType={1}
                key="consignment-list"
              />
            }
          />
          <Route
            path="rrs/recycling-consignment-detail/:id"
            element={<RecyclingConsignmentDetail />}
          />
          <Route
            path="rrs/recycling-consignment-order"
            element={<RecyclingConsignmentOrderPageV2 />}
          />
          <Route
            path="rrs/appointment-management-list"
            element={<AppointmentManagementList />}
          />
          <Route
            path="rrs/appointment-management-config"
            element={<AppointmentManagementConfig />}
          />

          <Route path="fms/account-list" element={<FmsAccountList />} />
          {/*<Route path="fms/shop-cash" element={<ShopCash />} />*/}
          {/*<Route path="fms/sys-summary" element={<SystemSummary />} />*/}
          <Route path="fms/cash-settlement" element={<CashSettlement />} />
          <Route path="fms/daily-report" element={<DailyReportPage />} />
          <Route path="fms/profit-report" element={<ProfitReport />} />
          <Route
            path="fms/commission-snapshot"
            element={<CommissionSnapshot />}
          />
          <Route
            path="fms/shop-sale-incentive"
            element={<ShopSaleIncentive />}
          />

          <Route
            path="cms/article-list"
            element={<CmsArticleListPage />}
          ></Route>
          <Route
            path="cms/article-detail"
            element={<CmsArticleDetailPage />}
          ></Route>
          <Route
            path="cms/article-category"
            element={<CmsArticleCategoryPage />}
          ></Route>
          <Route
            path="cms/subject-category"
            element={<CmsSubjectCategoryPage />}
          ></Route>
          <Route
            path="cms/subject-list"
            element={<CmsSubjectListPage />}
          ></Route>
          <Route
            path="cms/subject-detail"
            element={<CmsSubjectDetailPage />}
          ></Route>
          <Route
            path="cms/subject-product-relation/:id"
            element={<CmsSubjectProductRelationPage />}
          ></Route>
          <Route
            path="cms/special-contents"
            element={<CmsSpecialContentPage />}
          ></Route>

          <Route path="report/statement" element={<Statement />}></Route>
          <Route path="report/attendance" element={<Attendance />}></Route>

          <Route path="sys/admin-list" element={<AdminList />}></Route>
          <Route
            path="sys/admin-login-log"
            element={<AdminLoginLogPage />}
          ></Route>
          <Route
            path="sys/file-upload-log"
            element={<FileUploadLogPage />}
          ></Route>
          <Route path="sys/ip-blacklist" element={<IpBlacklistPage />}></Route>
          <Route path="sys/role" element={<RoleList />}></Route>
          <Route path="sys/menu" element={<MenuList />}></Route>
          <Route path="sys/resource" element={<ResourceList />}></Route>
          <Route path="sys/dict" element={<DictPage />}></Route>

          <Route path="sms/ad-list" element={<ADList />}></Route>
          <Route path="sms/ad-detail" element={<ADDetail />}></Route>

          <Route path="sop" element={<SOP />}></Route>
          <Route path="user-center" element={<PersonalOrderList />} />

          <Route
            path="*"
            element={
              <Navigate to={getLocalStorageFirstPage(userInfo.username)} />
            }
          />
        </Route>

        <Route path="/prints/price-tag" element={<PriceTagPrint />}></Route>
        <Route
          path="/prints/price-label"
          element={<PriceLabelPrinting />}
        ></Route>
        <Route path="/prints/receipt" element={<Receipt />}></Route>
        <Route path="/prints/dempyou" element={<DempyouPrint />}></Route>
        <Route
          path="/prints/sg-order-invoice/:orderId"
          element={<SGOrderInvoice />}
        ></Route>
        <Route
          path="/prints/sg-gx-order-invoice/:orderId"
          element={<SGGXOrderInvoice />}
        ></Route>
        <Route
          path="/prints/order-invoice-v2"
          element={<OrderInvoiceV2 />}
        ></Route>
        <Route
          path="/prints/oms-recycle-order-contract"
          element={<OmsRecycleOrderContract />}
        ></Route>
        <Route path="/prints/reception" element={<ReceptionPrint />} />
        <Route
          path="/prints/consignment-agreement"
          element={<ConsignmentAgreementPrint />}
        />
        <Route
          path="/rrs/recycling-consignment-detail/:id/reception"
          element={<RecycleOrderReceptionForm />}
        />
        <Route
          path="/rrs/reception/new"
          element={<RecycleOrderReceptionForm />}
        />
        <Route
          path="/rrs/reception/:receptionId"
          element={<RecycleOrderReceptionForm />}
        />
        <Route
          path="/rrs/consignment-agreement/:submissionId"
          element={<ConsignmentAgreementPage />}
        />

        <Route path={LOGIN_PAGE} element={<Login />} />
      </Route>,
    ),
  );

  return <RouterProvider router={router} />;
}

export default App;
